import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Archive,
  CheckCircle,
  Filter,
  Layers,
  PauseCircle,
  PlayCircle,
  Plus,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { EmptySessionsState } from '@/components/features/sessions/EmptySessionsState';
import { SessionCardSkeleton } from '@/components/features/sessions/SessionCardSkeleton';
import { SessionCard } from '@/components/shared/cards';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { SortControl, type SortDirection } from '@/components/ui/sort-control';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useGameDefinitions } from '@/hooks/useGameDefinitions';
import { usePlayers } from '@/hooks/usePlayers';
import { useRouteAnimation } from '@/hooks/useRouteAnimation';
import { useSessions } from '@/hooks/useSessions';
import { handleBackgroundError } from '@/lib/error-handler';
import { createGameConfigKey } from '@/lib/game-config';
import { slideUp, staggerConfig } from '@/lib/motion-config';
import { getSessionFilterDetails, type SessionFilterDetails } from '@/lib/session-filter-details';
import type {
  GameDefinition,
  GameDefinitionId,
  PlayerId,
  SessionId,
  SessionMetadata,
} from '@/schemas';

type SortOption = 'updatedAt' | 'createdAt' | 'name' | 'status' | 'playerCount';

const statusPriority: Record<SessionMetadata['status'], number> = {
  ongoing: 0,
  paused: 1,
  finished: 2,
};

const SESSION_SORT_DEFAULT_DIRECTION: Record<SortOption, SortDirection> = {
  updatedAt: 'desc',
  createdAt: 'desc',
  name: 'asc',
  status: 'asc',
  playerCount: 'desc',
};

const getUpdatedTimestamp = (session: SessionMetadata): number => {
  return session.updatedAt ?? session.startedAt;
};

const createSessionComparator = (sortOption: SortOption, direction: SortDirection) => {
  const multiplier = direction === 'asc' ? 1 : -1;
  switch (sortOption) {
    case 'name': {
      return (a: SessionMetadata, b: SessionMetadata) =>
        (a.name ?? '').toLowerCase().localeCompare((b.name ?? '').toLowerCase()) * multiplier;
    }
    case 'createdAt': {
      return (a: SessionMetadata, b: SessionMetadata) => (a.createdAt - b.createdAt) * multiplier;
    }
    case 'playerCount': {
      return (a: SessionMetadata, b: SessionMetadata) =>
        (a.playerCount - b.playerCount) * multiplier;
    }
    case 'status': {
      return (a: SessionMetadata, b: SessionMetadata) =>
        ((statusPriority[a.status] ?? 99) - (statusPriority[b.status] ?? 99)) * multiplier;
    }
    default: {
      return (a: SessionMetadata, b: SessionMetadata) =>
        (getUpdatedTimestamp(a) - getUpdatedTimestamp(b)) * multiplier;
    }
  }
};

type SessionStatusFilter = 'all' | 'ongoing' | 'paused' | 'finished' | 'archived';

interface BasicFilterOptions {
  normalizedSearch: string;
  statusFilter: SessionStatusFilter;
  startTimestamp?: number;
  endTimestamp?: number;
  minCount?: number;
  maxCount?: number;
}

const matchesStatusFilter = (
  session: SessionMetadata,
  statusFilter: SessionStatusFilter,
): boolean => {
  if (statusFilter === 'archived') {
    return Boolean(session.archivedAt);
  }

  // For 'all' and specific statuses, exclude archived sessions
  if (session.archivedAt) {
    return false;
  }

  // For specific statuses, match the status
  if (statusFilter !== 'all' && session.status !== statusFilter) {
    return false;
  }

  return true;
};

const matchesBasicFilters = (session: SessionMetadata, options: BasicFilterOptions): boolean => {
  const { normalizedSearch, statusFilter, startTimestamp, endTimestamp, minCount, maxCount } =
    options;

  if (normalizedSearch) {
    const name = session.name?.toLowerCase() ?? '';
    if (!name.includes(normalizedSearch)) {
      return false;
    }
  }

  if (!matchesStatusFilter(session, statusFilter)) {
    return false;
  }

  if (startTimestamp !== undefined && session.startedAt < startTimestamp) {
    return false;
  }

  if (endTimestamp !== undefined && session.startedAt > endTimestamp) {
    return false;
  }

  if (minCount !== undefined && session.playerCount < minCount) {
    return false;
  }

  if (maxCount !== undefined && session.playerCount > maxCount) {
    return false;
  }

  return true;
};

interface DetailFilterOptions {
  detailFiltersActive: boolean;
  definitionByKey: Map<string, GameDefinition>;
  selectedPresetId: 'all' | 'custom' | GameDefinitionId;
  hasRoundsOnly: boolean;
  teamsOnly: boolean;
  winConditionFilter: 'any' | 'mostPoints' | 'mostRounds';
  selectedPlayerSet: ReadonlySet<PlayerId>;
}

const matchesDetailFilters = (
  details: SessionFilterDetails | undefined,
  options: DetailFilterOptions,
): boolean => {
  const {
    detailFiltersActive,
    definitionByKey,
    selectedPresetId,
    hasRoundsOnly,
    teamsOnly,
    winConditionFilter,
    selectedPlayerSet,
  } = options;

  if (!details) {
    return !detailFiltersActive;
  }

  const matchingDefinition = definitionByKey.get(details.gameConfigKey);

  const presetIsCustom = selectedPresetId === 'custom';
  const presetIsSpecific = selectedPresetId !== 'all' && !presetIsCustom;

  if (
    (presetIsCustom && matchingDefinition) ||
    (presetIsSpecific && matchingDefinition?.id !== selectedPresetId)
  ) {
    return false;
  }

  if (
    (hasRoundsOnly && !details.gameConfig.hasRounds) ||
    (teamsOnly && !details.gameConfig.teamScoringType) ||
    (winConditionFilter !== 'any' && details.gameConfig.winCondition !== winConditionFilter)
  ) {
    return false;
  }

  if (selectedPlayerSet.size > 0) {
    const sessionPlayerSet = new Set(details.allPlayerIds);
    const hasAllPlayers = [...selectedPlayerSet].every((playerId) =>
      sessionPlayerSet.has(playerId),
    );
    if (!hasAllPlayers) {
      return false;
    }
  }

  return true;
};

const SessionsSearchSchema = z.object({
  search: z.string().optional(),
  status: z.enum(['all', 'ongoing', 'paused', 'finished', 'archived']).optional(),
});

type SessionsSearch = z.infer<typeof SessionsSearchSchema>;

export const Route = createFileRoute('/sessions/')({
  component: SessionsPage,
  validateSearch: (search: Record<string, unknown>): SessionsSearch => {
    const result = SessionsSearchSchema.safeParse(search);
    if (!result.success) {
      return {};
    }
    return {
      search: result.data.search,
      status: result.data.status ?? 'all',
    };
  },
});

function SessionsPage() {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const statusFilter = search.status ?? 'all';
  const selectedStatus = statusFilter;
  const { sessions, isInitialLoading, hasData, hasLoadedOnce, refresh } = useSessions();
  // Load all players to check if "New Session" button should be visible (based on raw/unfiltered data)
  const {
    players: allPlayers,
    hasData: hasAnyPlayers,
    isLoading: isLoadingPlayers,
  } = usePlayers({ includeDeleted: true });
  const { gameDefinitions } = useGameDefinitions();
  const [searchInput, setSearchInput] = useState(search.search ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(search.search ?? '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [playerCountMin, setPlayerCountMin] = useState('');
  const [playerCountMax, setPlayerCountMax] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState<'all' | 'custom' | GameDefinitionId>(
    'all',
  );
  const [hasRoundsOnly, setHasRoundsOnly] = useState(false);
  const [teamsOnly, setTeamsOnly] = useState(false);
  const [winConditionFilter, setWinConditionFilter] = useState<'any' | 'mostPoints' | 'mostRounds'>(
    'any',
  );
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<PlayerId[]>([]);
  const [sortOption, setSortOption] = useState<
    'updatedAt' | 'createdAt' | 'name' | 'status' | 'playerCount'
  >('updatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SESSION_SORT_DEFAULT_DIRECTION.updatedAt,
  );
  const [isAdvancedPopoverOpen, setIsAdvancedPopoverOpen] = useState(false);
  const [isAdvancedDrawerOpen, setIsAdvancedDrawerOpen] = useState(false);
  const [sessionDetailsMap, setSessionDetailsMap] = useState<Map<SessionId, SessionFilterDetails>>(
    new Map(),
  );

  useDocumentTitle(`${t('sessions.title', 'Sessions')} - ${t('app.name', 'One Score')}`);

  const { shouldAnimate, routeKey } = useRouteAnimation();

  const sessionSortOptions = useMemo(
    () => [
      { value: 'updatedAt', label: t('sessions.filters.sort.updatedAt') },
      { value: 'createdAt', label: t('sessions.filters.sort.createdAt') },
      { value: 'name', label: t('sessions.filters.sort.name') },
      { value: 'status', label: t('sessions.filters.sort.status') },
      { value: 'playerCount', label: t('sessions.filters.sort.playerCount') },
    ],
    [t],
  );

  const sortDirectionLabels = useMemo(
    () => ({
      asc: t('filters.sort.direction.asc'),
      desc: t('filters.sort.direction.desc'),
    }),
    [t],
  );

  const handleSortOptionChange = useCallback((value: string) => {
    const nextValue = (value || 'updatedAt') as SortOption;
    setSortOption(nextValue);
    setSortDirection(SESSION_SORT_DEFAULT_DIRECTION[nextValue]);
  }, []);

  const handleSortDirectionToggle = useCallback(() => {
    setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchInput]);

  // Update URL params when filters change
  useEffect(() => {
    void navigate({
      search: (prev) => ({
        ...prev,
        search: debouncedSearch || undefined,
        status: statusFilter,
      }),
      replace: true,
    });
  }, [debouncedSearch, statusFilter, navigate]);

  useEffect(() => {
    let isCancelled = false;

    const run = async () => {
      if (sessions.length === 0) {
        setSessionDetailsMap((previous) => {
          if (previous.size === 0) {
            return previous;
          }
          return new Map<SessionId, SessionFilterDetails>();
        });
        return;
      }

      const entries = await Promise.all(
        sessions.map(async (session) => {
          try {
            const details = await getSessionFilterDetails(session.id);
            return [session.id, details] as const;
          } catch (error) {
            handleBackgroundError(error, {
              operation: 'loadSessionFilters',
              sessionId: session.id,
            });
            return;
          }
        }),
      );

      if (isCancelled) {
        return;
      }

      const nextMap = new Map<SessionId, SessionFilterDetails>();
      for (const entry of entries) {
        if (!entry) continue;
        nextMap.set(entry[0], entry[1]);
      }
      setSessionDetailsMap(nextMap);
    };

    queueMicrotask(() => {
      if (!isCancelled) {
        void run();
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [sessions]);

  const handleStatusChange = useCallback(
    (value: string) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          status: (value as 'all' | 'ongoing' | 'paused' | 'finished' | 'archived') ?? 'all',
        }),
      });
    },
    [navigate],
  );

  const handlePlayerToggle = useCallback((playerId: PlayerId, checked: boolean) => {
    setSelectedPlayerIds((prev) => {
      if (checked) {
        if (prev.includes(playerId)) {
          return prev;
        }
        return [...prev, playerId];
      }
      return prev.filter((id) => id !== playerId);
    });
  }, []);

  const playerLookup = useMemo(() => {
    return new Map(allPlayers.map((player) => [player.id, player]));
  }, [allPlayers]);

  const playerOptions = useMemo(() => {
    return allPlayers
      .toSorted((a, b) => a.name.localeCompare(b.name))
      .map((player) => ({
        id: player.id,
        name: player.name,
      }));
  }, [allPlayers]);

  const definitionByKey = useMemo(() => {
    const map = new Map<string, GameDefinition>();
    for (const definition of gameDefinitions) {
      const key = createGameConfigKey({
        hasRounds: definition.hasRounds,
        winCondition: definition.winCondition,
        roundTargetPoints: definition.roundTargetPoints,
        gameTargetPoints: definition.gameTargetPoints,
        targetRounds: definition.targetRounds,
        teamScoringType: definition.teamScoringType,
        version: definition.version,
      });
      map.set(key, definition);
    }
    return map;
  }, [gameDefinitions]);

  const gameDefinitionById = useMemo(() => {
    return new Map<GameDefinitionId, GameDefinition>(
      gameDefinitions.map((definition) => [definition.id, definition]),
    );
  }, [gameDefinitions]);

  const detailFiltersActive = useMemo(
    () =>
      selectedPresetId !== 'all' ||
      hasRoundsOnly ||
      teamsOnly ||
      winConditionFilter !== 'any' ||
      selectedPlayerIds.length > 0,
    [selectedPresetId, hasRoundsOnly, teamsOnly, winConditionFilter, selectedPlayerIds.length],
  );

  const selectedPlayerSet = useMemo(
    () => new Set<PlayerId>(selectedPlayerIds),
    [selectedPlayerIds],
  );

  const filteredSessions = useMemo(() => {
    const normalizedSearch = debouncedSearch.trim().toLowerCase();
    const startTimestamp = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : undefined;
    const endTimestamp = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : undefined;
    const minCount = playerCountMin ? Number(playerCountMin) : undefined;
    const maxCount = playerCountMax ? Number(playerCountMax) : undefined;

    const basicOptions: BasicFilterOptions = {
      normalizedSearch,
      statusFilter,
      startTimestamp,
      endTimestamp,
      minCount: Number.isNaN(minCount ?? Number.NaN) ? undefined : minCount,
      maxCount: Number.isNaN(maxCount ?? Number.NaN) ? undefined : maxCount,
    };

    const detailOptions: DetailFilterOptions = {
      detailFiltersActive,
      definitionByKey,
      selectedPresetId,
      hasRoundsOnly,
      teamsOnly,
      winConditionFilter,
      selectedPlayerSet,
    };

    const comparator = createSessionComparator(sortOption, sortDirection);

    return sessions
      .filter((session) => {
        if (!matchesBasicFilters(session, basicOptions)) {
          return false;
        }

        const details = sessionDetailsMap.get(session.id);
        return matchesDetailFilters(details, detailOptions);
      })
      .toSorted(comparator);
  }, [
    sessions,
    debouncedSearch,
    statusFilter,
    startDate,
    endDate,
    playerCountMin,
    playerCountMax,
    sessionDetailsMap,
    detailFiltersActive,
    definitionByKey,
    selectedPresetId,
    hasRoundsOnly,
    teamsOnly,
    winConditionFilter,
    selectedPlayerSet,
    sortOption,
    sortDirection,
  ]);

  const selectedPlayerBadges = selectedPlayerIds.map((playerId) => {
    const player = playerLookup.get(playerId);
    return {
      id: playerId,
      name: player?.name ?? t('sessions.filters.players.unknown'),
    };
  });

  const isSearchActive = useMemo(() => Boolean(debouncedSearch.trim()), [debouncedSearch]);

  const hasAdvancedFilters = useMemo(
    () =>
      Boolean(startDate) ||
      Boolean(endDate) ||
      Boolean(playerCountMin) ||
      Boolean(playerCountMax) ||
      detailFiltersActive,
    [startDate, endDate, playerCountMin, playerCountMax, detailFiltersActive],
  );

  const advancedFilterCount = useMemo(
    () =>
      (startDate || endDate ? 1 : 0) +
      (playerCountMin || playerCountMax ? 1 : 0) +
      (selectedPresetId === 'all' ? 0 : 1) +
      (hasRoundsOnly ? 1 : 0) +
      (teamsOnly ? 1 : 0) +
      (winConditionFilter === 'any' ? 0 : 1) +
      (selectedPlayerIds.length > 0 ? 1 : 0),
    [
      startDate,
      endDate,
      playerCountMin,
      playerCountMax,
      selectedPresetId,
      hasRoundsOnly,
      teamsOnly,
      winConditionFilter,
      selectedPlayerIds.length,
    ],
  );

  const hasActiveFilters = useMemo(
    () =>
      isSearchActive ||
      statusFilter !== 'all' ||
      Boolean(startDate) ||
      Boolean(endDate) ||
      Boolean(playerCountMin) ||
      Boolean(playerCountMax) ||
      selectedPresetId !== 'all' ||
      hasRoundsOnly ||
      teamsOnly ||
      winConditionFilter !== 'any' ||
      selectedPlayerIds.length > 0 ||
      sortOption !== 'updatedAt' ||
      sortDirection !== SESSION_SORT_DEFAULT_DIRECTION[sortOption],
    [
      isSearchActive,
      statusFilter,
      startDate,
      endDate,
      playerCountMin,
      playerCountMax,
      selectedPresetId,
      hasRoundsOnly,
      teamsOnly,
      winConditionFilter,
      selectedPlayerIds.length,
      sortOption,
      sortDirection,
    ],
  );

  const clearAdvancedFilters = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setPlayerCountMin('');
    setPlayerCountMax('');
    setSelectedPresetId('all');
    setHasRoundsOnly(false);
    setTeamsOnly(false);
    setWinConditionFilter('any');
    setSelectedPlayerIds([]);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
    if (statusFilter !== 'all') {
      handleStatusChange('all');
    }
    clearAdvancedFilters();
    setSortOption('updatedAt');
    setSortDirection(SESSION_SORT_DEFAULT_DIRECTION.updatedAt);
    setIsAdvancedPopoverOpen(false);
    setIsAdvancedDrawerOpen(false);
  }, [clearAdvancedFilters, handleStatusChange, statusFilter]);

  const renderAdvancedFilters = () => (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label>{t('sessions.filters.dateRange')}</Label>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            aria-label={t('sessions.filters.dateStart')}
          />
          <Input
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            aria-label={t('sessions.filters.dateEnd')}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t('sessions.filters.preset.label')}</Label>
        <Select
          value={selectedPresetId}
          onValueChange={(value) => setSelectedPresetId(value as typeof selectedPresetId)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('sessions.filters.preset.label')} />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">{t('sessions.filters.preset.all')}</SelectItem>
            <SelectItem value="custom">{t('sessions.filters.preset.custom')}</SelectItem>
            {gameDefinitions.map((definition) => (
              <SelectItem key={definition.id} value={definition.id}>
                {definition.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>{t('sessions.filters.winCondition.label')}</Label>
        <Select
          value={winConditionFilter}
          onValueChange={(value) => setWinConditionFilter(value as typeof winConditionFilter)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('sessions.filters.winCondition.label')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{t('sessions.filters.winCondition.any')}</SelectItem>
            <SelectItem value="mostPoints">
              {t('sessions.filters.winCondition.mostPoints')}
            </SelectItem>
            <SelectItem value="mostRounds">
              {t('sessions.filters.winCondition.mostRounds')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>{t('sessions.filters.playerCount.label')}</Label>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            min={0}
            value={playerCountMin}
            onChange={(event) => setPlayerCountMin(event.target.value)}
            placeholder={t('sessions.filters.playerCount.min')}
          />
          <Input
            type="number"
            min={0}
            value={playerCountMax}
            onChange={(event) => setPlayerCountMax(event.target.value)}
            placeholder={t('sessions.filters.playerCount.max')}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t('sessions.filters.players.label')}</Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <span>
                {selectedPlayerIds.length === 0
                  ? t('sessions.filters.players.placeholder')
                  : t('sessions.filters.players.selected', {
                      count: selectedPlayerIds.length,
                    })}
              </span>
              <SlidersHorizontal className="ml-2 h-4 w-4 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64 max-h-72 overflow-y-auto" align="start">
            {playerOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {t('sessions.filters.players.empty')}
              </div>
            ) : (
              playerOptions.map((player) => (
                <DropdownMenuCheckboxItem
                  key={player.id}
                  checked={selectedPlayerIds.includes(player.id)}
                  onCheckedChange={(checked) => handlePlayerToggle(player.id, checked === true)}
                >
                  {player.name}
                </DropdownMenuCheckboxItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="space-y-3 rounded-lg border p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-1">
            <p className="text-sm font-medium">{t('sessions.filters.hasRounds.label')}</p>
            <p className="text-xs text-muted-foreground">
              {t('sessions.filters.hasRounds.description')}
            </p>
          </div>
          <Switch checked={hasRoundsOnly} onCheckedChange={setHasRoundsOnly} />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-1">
            <p className="text-sm font-medium">{t('sessions.filters.teams.label')}</p>
            <p className="text-xs text-muted-foreground">
              {t('sessions.filters.teams.description')}
            </p>
          </div>
          <Switch checked={teamsOnly} onCheckedChange={setTeamsOnly} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-6xl space-y-6 px-4 mx-auto">
      {/* Header Section */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('sessions.title', 'Sessions')}</h1>
          <p className="text-muted-foreground">{t('sessions.description')}</p>
        </div>
        {!isInitialLoading && !isLoadingPlayers && hasAnyPlayers && allPlayers.length > 0 && (
          <Link to="/sessions/new" className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              {t('sessions.newButton', 'New Session')}
            </Button>
          </Link>
        )}
      </div>

      {hasLoadedOnce && sessions.length >= 3 && (
        <div className="space-y-3 rounded-lg bg-card shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full min-w-0 flex-1 sm:max-w-sm sm:min-w-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={
                  !isInitialLoading && sessions.length > 0
                    ? (() => {
                        if (hasActiveFilters) {
                          return t('sessions.searchWithCountFiltered', {
                            count: filteredSessions.length,
                            filtered: filteredSessions.length,
                            total: sessions.length,
                          });
                        }
                        return t('sessions.searchWithCount', { count: sessions.length });
                      })()
                    : t('sessions.searchPlaceholder')
                }
                aria-label={t('sessions.filters.search.label')}
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                className="h-10 pl-9 min-w-0"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:flex-shrink-0">
              <Tabs
                value={statusFilter}
                onValueChange={handleStatusChange}
                className="w-full sm:w-auto sm:flex-none sm:flex-shrink-0"
                aria-label={t('sessions.filters.status.label')}
              >
                <TabsList className="flex h-10 w-full flex-wrap gap-1 sm:w-auto">
                  {isMobile ? (
                    <SimpleTooltip content={t('sessions.status.all')}>
                      <TabsTrigger
                        value="all"
                        aria-label={t('sessions.status.all')}
                        className="aria-selected:bg-primary-foreground aria-selected:text-primary aria-selected:shadow-md"
                      >
                        <Layers className="h-4 w-4" />
                      </TabsTrigger>
                    </SimpleTooltip>
                  ) : (
                    <TabsTrigger value="all">{t('sessions.status.all')}</TabsTrigger>
                  )}
                  {isMobile ? (
                    <SimpleTooltip content={t('sessions.status.ongoing')}>
                      <TabsTrigger
                        value="ongoing"
                        aria-label={t('sessions.status.ongoing')}
                        className="aria-selected:bg-primary-foreground aria-selected:text-primary aria-selected:shadow-md"
                      >
                        <PlayCircle className="h-4 w-4" />
                      </TabsTrigger>
                    </SimpleTooltip>
                  ) : (
                    <TabsTrigger value="ongoing">{t('sessions.status.ongoing')}</TabsTrigger>
                  )}
                  {isMobile ? (
                    <SimpleTooltip content={t('sessions.status.paused')}>
                      <TabsTrigger
                        value="paused"
                        aria-label={t('sessions.status.paused')}
                        className="aria-selected:bg-primary-foreground aria-selected:text-primary aria-selected:shadow-md"
                      >
                        <PauseCircle className="h-4 w-4" />
                      </TabsTrigger>
                    </SimpleTooltip>
                  ) : (
                    <TabsTrigger value="paused">{t('sessions.status.paused')}</TabsTrigger>
                  )}
                  {isMobile ? (
                    <SimpleTooltip content={t('sessions.status.finished')}>
                      <TabsTrigger
                        value="finished"
                        aria-label={t('sessions.status.finished')}
                        className="aria-selected:bg-primary-foreground aria-selected:text-primary aria-selected:shadow-md"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </TabsTrigger>
                    </SimpleTooltip>
                  ) : (
                    <TabsTrigger value="finished">{t('sessions.status.finished')}</TabsTrigger>
                  )}
                  {isMobile ? (
                    <SimpleTooltip content={t('sessions.status.archived', 'Archived')}>
                      <TabsTrigger
                        value="archived"
                        aria-label={t('sessions.status.archived', 'Archived')}
                        className="aria-selected:bg-primary-foreground aria-selected:text-primary aria-selected:shadow-md"
                      >
                        <Archive className="h-4 w-4" />
                      </TabsTrigger>
                    </SimpleTooltip>
                  ) : (
                    <TabsTrigger value="archived">
                      {t('sessions.status.archived', 'Archived')}
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>

              <div className="flex w-full items-center gap-2 sm:w-auto sm:flex-none">
                <div className="w-full sm:w-10 sm:min-w-10 sm:flex-shrink-0">
                  <SortControl
                    label={t('sessions.filters.sort.label')}
                    value={sortOption}
                    direction={sortDirection}
                    options={sessionSortOptions}
                    onValueChange={handleSortOptionChange}
                    onDirectionToggle={handleSortDirectionToggle}
                    directionLabels={sortDirectionLabels}
                    triggerClassName="h-10 w-full sm:w-10 sm:min-w-10 flex-shrink-0"
                  />
                </div>

                <div className="hidden md:block flex-shrink-0 w-10 min-w-10">
                  <Popover open={isAdvancedPopoverOpen} onOpenChange={setIsAdvancedPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant={hasAdvancedFilters ? 'secondary' : 'outline'}
                        size="icon"
                        className="h-10 w-10 min-w-10 flex-shrink-0"
                        aria-label={t('sessions.filters.more')}
                      >
                        <Filter className="h-4 w-4" />
                        {advancedFilterCount > 0 && (
                          <Badge
                            variant="secondary"
                            className="pointer-events-none absolute -right-2 -top-2 h-5 min-w-[1.25rem] rounded-full px-1 text-[10px]"
                          >
                            {advancedFilterCount}
                          </Badge>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[360px] max-h-[70vh] overflow-y-auto p-0"
                      align="end"
                    >
                      <div className="space-y-4 p-4">
                        {renderAdvancedFilters()}
                        <div className="flex items-center justify-between border-t pt-3">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={clearAdvancedFilters}
                            disabled={!hasAdvancedFilters}
                          >
                            {t('sessions.filters.resetAdvanced')}
                          </Button>
                          <PopoverClose asChild>
                            <Button type="button" size="sm">
                              {t('sessions.filters.done')}
                            </Button>
                          </PopoverClose>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="md:hidden w-full sm:w-10 sm:min-w-10 sm:flex-shrink-0">
                  <Drawer open={isAdvancedDrawerOpen} onOpenChange={setIsAdvancedDrawerOpen}>
                    <DrawerTrigger asChild>
                      <Button
                        type="button"
                        variant={hasAdvancedFilters ? 'secondary' : 'outline'}
                        className="relative flex h-10 w-full sm:w-10 sm:min-w-10 items-center justify-center p-0 flex-shrink-0"
                        aria-label={t('sessions.filters.more')}
                      >
                        <Filter className="h-4 w-4" />
                        {advancedFilterCount > 0 && (
                          <Badge
                            variant="secondary"
                            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 translate-x-1/2 rounded-full px-2 py-0 text-xs"
                          >
                            {advancedFilterCount}
                          </Badge>
                        )}
                      </Button>
                    </DrawerTrigger>
                    <DrawerContent className="flex max-h-[85vh] flex-col">
                      <DrawerHeader>
                        <DrawerTitle>{t('sessions.filters.advancedTitle')}</DrawerTitle>
                        <DrawerDescription>
                          {t('sessions.filters.advancedDescription')}
                        </DrawerDescription>
                      </DrawerHeader>
                      <div className="flex-1 space-y-4 overflow-y-auto px-4 pb-4">
                        {renderAdvancedFilters()}
                      </div>
                      <DrawerFooter>
                        <Button
                          variant="outline"
                          onClick={() => {
                            clearAdvancedFilters();
                          }}
                          disabled={!hasAdvancedFilters}
                        >
                          {t('sessions.filters.resetAdvanced')}
                        </Button>
                        <Button
                          onClick={() => {
                            setIsAdvancedDrawerOpen(false);
                          }}
                        >
                          {t('sessions.filters.apply')}
                        </Button>
                      </DrawerFooter>
                    </DrawerContent>
                  </Drawer>
                </div>
              </div>
            </div>
          </div>

          {(statusFilter !== 'all' || hasAdvancedFilters || isSearchActive) && (
            <div className="flex flex-wrap items-center gap-2">
              {isSearchActive && (
                <Badge variant="outline">
                  {t('sessions.filters.search.badge', { value: searchInput.trim() })}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-1 h-4 w-4 p-0"
                    onClick={() => setSearchInput('')}
                    aria-label={t('sessions.filters.search.clear')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
              {statusFilter !== 'all' && (
                <Badge variant="secondary">
                  {t(`sessions.status.${statusFilter}`)}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-2 h-4 w-4 p-0"
                    onClick={() => handleStatusChange('all')}
                    aria-label={t('sessions.filters.status.clear')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
              {(startDate || endDate) && (
                <Badge variant="outline">
                  {t('sessions.filters.badges.date', {
                    start: startDate || t('sessions.filters.badges.openStart'),
                    end: endDate || t('sessions.filters.badges.openEnd'),
                  })}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-1 h-4 w-4 p-0"
                    onClick={() => {
                      setStartDate('');
                      setEndDate('');
                    }}
                    aria-label={t('sessions.filters.badges.clearDate')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
              {(playerCountMin || playerCountMax) && (
                <Badge variant="outline">
                  {t('sessions.filters.badges.playerCount', {
                    min: playerCountMin || t('sessions.filters.badges.any'),
                    max: playerCountMax || t('sessions.filters.badges.any'),
                  })}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-1 h-4 w-4 p-0"
                    onClick={() => {
                      setPlayerCountMin('');
                      setPlayerCountMax('');
                    }}
                    aria-label={t('sessions.filters.badges.removePlayerCount')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
              {selectedPresetId !== 'all' && (
                <Badge variant="outline" className="gap-1">
                  {(() => {
                    if (selectedPresetId === 'custom') {
                      return t('sessions.filters.preset.custom');
                    }
                    const presetDefinition = gameDefinitionById.get(selectedPresetId);
                    return presetDefinition?.name ?? t('sessions.filters.preset.unknown');
                  })()}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-1 h-4 w-4 p-0"
                    onClick={() => setSelectedPresetId('all')}
                    aria-label={t('sessions.filters.badges.removePreset')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
              {hasRoundsOnly && (
                <Badge variant="outline">
                  {t('sessions.filters.hasRounds.badge')}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-1 h-4 w-4 p-0"
                    onClick={() => setHasRoundsOnly(false)}
                    aria-label={t('sessions.filters.badges.removeHasRounds')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
              {teamsOnly && (
                <Badge variant="outline">
                  {t('sessions.filters.teams.badge')}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-1 h-4 w-4 p-0"
                    onClick={() => setTeamsOnly(false)}
                    aria-label={t('sessions.filters.badges.removeTeams')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
              {winConditionFilter !== 'any' && (
                <Badge variant="outline">
                  {t(`sessions.filters.winCondition.${winConditionFilter}`)}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-1 h-4 w-4 p-0"
                    onClick={() => setWinConditionFilter('any')}
                    aria-label={t('sessions.filters.badges.removeWinCondition')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
              {selectedPlayerBadges.map((player) => (
                <Badge key={player.id} variant="outline" className="gap-1">
                  {player.name}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-1 h-4 w-4 p-0"
                    onClick={() => handlePlayerToggle(player.id, false)}
                    aria-label={t('sessions.filters.badges.removePlayer', { player: player.name })}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  {t('sessions.filters.clear')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {isInitialLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <SessionCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isInitialLoading && !hasData && selectedStatus !== 'archived' && <EmptySessionsState />}
      {!isInitialLoading && !hasData && selectedStatus === 'archived' && (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Archive className="h-8 w-8" />
            </EmptyMedia>
            <EmptyTitle>{t('sessions.archivedEmptyTitle', 'No archived sessions')}</EmptyTitle>
            <EmptyDescription>
              {t(
                'sessions.archivedEmptyDescription',
                'Archive a finished session to keep it around without cluttering your active list.',
              )}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}

      {/* Sessions Grid */}
      {!isInitialLoading && filteredSessions.length > 0 && (
        <motion.div
          key={`sessions-grid-${routeKey}`}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          variants={slideUp}
          initial={shouldAnimate ? 'initial' : false}
          animate="animate"
          transition={staggerConfig.container}
        >
          <AnimatePresence mode="popLayout">
            {filteredSessions.map((session) => (
              <motion.div
                key={session.id}
                layout
                layoutId={`session-card-${session.id}`}
                variants={slideUp}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <SessionCard
                  session={session}
                  onSessionChange={() => {
                    void refresh();
                  }}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* No Results */}
      {!isInitialLoading && hasData && filteredSessions.length === 0 && (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Search className="h-8 w-8" />
            </EmptyMedia>
            <EmptyTitle>{t('sessions.noResults', 'No sessions match your search')}</EmptyTitle>
            <EmptyDescription>
              {t(
                'sessions.noResultsDescription',
                "Try adjusting your filters or search terms to find what you're looking for.",
              )}
            </EmptyDescription>
          </EmptyHeader>
          {hasActiveFilters && (
            <EmptyContent>
              <Button variant="outline" onClick={handleClearFilters}>
                {t('sessions.filters.clear')}
              </Button>
            </EmptyContent>
          )}
        </Empty>
      )}
    </div>
  );
}
